import { HttpClient } from '@angular/common/http';
import { Sort } from '@angular/material/sort';
import {
  BaseClassConstructor,
  checkNoUndefinedFieldsArray,
  Data,
  field,
  NoUndefinedFields,
  primaryKey,
  Response,
  waitAndRetry,
} from '@dimandzhi/graphql';
import { currentMethodName } from 'app/current-method-name';
import { GraphQL } from 'app/graphql';
import _ from 'lodash';
import { map, throwError } from 'rxjs';

const gqlType = GraphQL.Fragment;
type gqlType = GraphQL.Fragment;

export class Fragments extends gqlType.createServiceBaseConstructor() { }

export class FragmentWithProblem extends BaseClassConstructor<FragmentWithProblem>('Fragment')() {
  @primaryKey() id?: number;
  @field() channelStreamID?: number;
  @field() start?: string;
  @field() durationSec?: number;
  @field() taskID?: number | null;
  @field() wordsCountInAsrText?: number | null;
  @field() fileName?: string;
  @field() failCause?: string[] | null;
  @field() durationTooShort?: boolean;
  @field() durationTooLong?: boolean;
  @field() hasFailCause?: boolean;
}

export class FragmentsWithProblem {
  constructor(
    protected http: HttpClient,
    public readonly gqlUrl: string,
  ) { }

  deleteWithFile(id: number) {
    if (!_.isFinite(id)) {
      return throwError(() => `invalid "id" argument (${id})`);
    }
    return this.http
      .post<Response>(
        `${this.gqlUrl}/${FragmentWithProblem.getModelName()}-${currentMethodName()}`,
        `mutation {
          delete {
            FragmentWithFile(id: ${id})
          }
        }`,
      ).pipe(
        waitAndRetry,
        Data.fromResp,
        map(data => {
          let result: 1 | 0 | undefined = _.get(data.getResp(), ['delete', 'FragmentWithFile'], undefined);
          if (!_.isFinite(result)) {
            return throwError(() => `invalid "result" argument (${result})`);
          }
          return result;
        }),
      );
  }

  getWithProblems(
    limit: number,
    maxFails?: number,
    shortestDuration?: number,
    longestDuration?: number,
    cursor: string | null = null,
    sort?: Sort,
    filter?: string,
  ) {
    let args: string[] = [];
    if (_.isNumber(limit) && limit > 0 && limit <= 100) {
      args.push(`limit: ${limit}`);
    } else {
      return throwError(() => `invalid "limit" argument (${limit})`);
    }
    if (_.isNumber(maxFails) && maxFails >= 0) {
      args.push(`maxFails: ${maxFails}`);
    } else {
      return throwError(() => `invalid "maxFails" argument (${maxFails})`);
    }
    if (!_.isNumber(shortestDuration) || shortestDuration <= 0) {
      return throwError(() => `invalid "shortestDuration" argument (${shortestDuration})`);
    }
    if (!_.isNumber(longestDuration) || longestDuration <= 0) {
      return throwError(() => `invalid "longestDuration" argument (${longestDuration})`);
    }
    if (_.isNumber(shortestDuration) && _.isNumber(longestDuration) && longestDuration < shortestDuration) {
      return throwError(
        () =>
          `"longestDuration" is shorter than "shortestDuration"` +
          ` (shortestDuration=${shortestDuration} and longestDuration=${longestDuration})`,
      );
    }
    if (_.isNumber(shortestDuration)) {
      args.push(`shortestDuration: ${shortestDuration}`);
    }
    if (_.isNumber(longestDuration)) {
      args.push(`longestDuration: ${longestDuration}`);
    }
    if (_.isString(cursor)) {
      args.push(`cursor: "${cursor}"`);
    }
    if (_.isObject(sort)) {
      args.push(`sorting: {field: ${sort.active}, order: ${sort.direction.toUpperCase()}}`);
    }
    if (_.isString(filter)) {
      args.push(`search: "${filter}"`);
    }
    const queryField = 'withProblems';
    return this.http
      .post<Response>(
        `${this.gqlUrl}/${FragmentWithProblem.getModelName()}-${currentMethodName()}`,
        `query {
          ${gqlType.getModelName()} {
            ${queryField}(${args.join(', ')}) {
              count
              pagination {
                cursor
                hasNext
              }
              data {
                ${FragmentWithProblem.fields({ withSubFields: true }).join(' ')}
              }
            }
          }
        }`,
      )
      .pipe(
        waitAndRetry,
        Data.fromResp,
        map((data) => {
          let result: FullData<any> = _.get(data.getResp(), [gqlType.getModelName(), queryField], undefined);
          result.data = FragmentWithProblem.fromData(data, queryField + '.data');
          checkNoUndefinedFieldsArray(result.data);
          return result as FullData<NoUndefinedFields<FragmentWithProblem>>;
        }),
        // noUndefinedFields(),
      );
  }
}

interface FullData<T> {
  count: number;
  pagination: {
    cursor: string | null;
    hasNext: boolean;
  };
  data: T[];
}
